import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon } from '@heroicons/react/24/outline';
import ButtonGroup from '../../../ui/atoms/ButtonGroup';
import { Button } from '../../../ui/atoms';
import QuestionList from './QuestionList';
import QuestionDrawer from './QuestionDrawer';

const INPUT_CLASSES = "form-input-inset";
const SELECT_CLASSES = "form-select-inset";

const AVAILABLE_LANGUAGES = [
  { code: 'pl-pl', name: 'Polski' },
  { code: 'en-us', name: 'English' },
  { code: 'de-de', name: 'Deutsch' },
  { code: 'es-es', name: 'Espanol' },
  { code: 'cs-cz', name: 'Cestina' },
  { code: 'sk-sk', name: 'Slovencina' },
  { code: 'sl-si', name: 'Slovenscina' }
];

const WidgetSettings = ({ widgetConfig, onConfigChange, setWidgetConfig, onSave, saving, onRequestPreviewMode, focusSection = null, behaviorOnly = false }) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState({ question: '', prompt: '' });
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(-1);
  const [showQuestionDrawer, setShowQuestionDrawer] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add');
  const [isEditingBasic, setIsEditingBasic] = useState(false);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [isEditingBehavior, setIsEditingBehavior] = useState(false);
  
  // Switch preview to conversation mode when editing content
  const handleEditContent = () => {
    setIsEditingContent(true);
    if (onRequestPreviewMode) {
      onRequestPreviewMode('conversation');
    }
  };
  
  // Switch preview to intro mode when editing basic settings
  const handleEditBasic = () => {
    setIsEditingBasic(true);
    if (onRequestPreviewMode) {
      onRequestPreviewMode('intro');
    }
  };
  
  // Switch back to intro when canceling content edit
  const handleCancelContentEdit = () => {
    setIsEditingContent(false);
    if (onRequestPreviewMode) {
      onRequestPreviewMode('intro');
    }
  };

  const handleQuestionChange = (e) => {
    const { name, value } = e.target;
    setCurrentQuestion(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addQuestion = () => {
    if (currentQuestion.question.trim() === '') return;
    
    if (editingQuestionIndex >= 0) {
      // Update existing question
      const updatedQuestions = [...widgetConfig.widget_questions];
      updatedQuestions[editingQuestionIndex] = {
        question: currentQuestion.question,
        prompt: currentQuestion.prompt || currentQuestion.question
      };
      
      setWidgetConfig(prev => ({
        ...prev,
        widget_questions: updatedQuestions
      }));
      
      setEditingQuestionIndex(-1);
    } else {
      // Add new question
      setWidgetConfig(prev => ({
        ...prev,
        widget_questions: [...(prev.widget_questions || []), {
          question: currentQuestion.question,
          prompt: currentQuestion.prompt || currentQuestion.question
        }]
      }));
    }
    
    // Clear the form and close drawer
    setCurrentQuestion({ question: '', prompt: '' });
    setShowQuestionDrawer(false);
    setDrawerMode('add');
  };

  const editQuestion = (index, newQuestion, newPrompt) => {
    // Inline editing - update question and prompt directly
    const updatedQuestions = [...widgetConfig.widget_questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      question: newQuestion !== undefined ? newQuestion : updatedQuestions[index].question,
      prompt: newPrompt !== undefined ? newPrompt : (updatedQuestions[index].prompt || updatedQuestions[index].question)
    };
    
    setWidgetConfig(prev => ({
      ...prev,
      widget_questions: updatedQuestions
    }));
  };

  const reorderQuestions = (oldIndex, newIndex) => {
    const reorderedQuestions = [...widgetConfig.widget_questions];
    const [movedQuestion] = reorderedQuestions.splice(oldIndex, 1);
    reorderedQuestions.splice(newIndex, 0, movedQuestion);
    
    setWidgetConfig(prev => ({
      ...prev,
      widget_questions: reorderedQuestions
    }));
  };

  const cancelEdit = () => {
    setCurrentQuestion({ question: '', prompt: '' });
    setEditingQuestionIndex(-1);
    setShowQuestionDrawer(false);
    setDrawerMode('add');
  };

  const openAddQuestionDrawer = () => {
    setCurrentQuestion({ question: '', prompt: '' });
    setEditingQuestionIndex(-1);
    setDrawerMode('add');
    setShowQuestionDrawer(true);
  };

  const removeQuestion = (index) => {
    setWidgetConfig(prev => ({
      ...prev,
      widget_questions: prev.widget_questions.filter((_, i) => i !== index)
    }));
    
    // If we're currently editing this question, reset the form
    if (editingQuestionIndex === index) {
      setCurrentQuestion({ question: '', prompt: '' });
      setEditingQuestionIndex(-1);
    }
  };

  const handleSaveBasic = async () => {
    await onSave();
    setIsEditingBasic(false);
  };

  const handleSaveContent = async () => {
    await onSave();
    setIsEditingContent(false);
    // Keep preview in conversation mode after save
  };

  const handleSaveBehavior = async () => {
    await onSave();
    setIsEditingBehavior(false);
  };

  useEffect(() => {
    if (behaviorOnly) return;
    if (focusSection !== 'behavior') return;
    setIsEditingContent(true);
    if (onRequestPreviewMode) {
      onRequestPreviewMode('conversation');
    }
    requestAnimationFrame(() => {
      const promptField = document.getElementById('widget_prompt');
      if (promptField) {
        promptField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, [focusSection, onRequestPreviewMode, behaviorOnly]);

  if (behaviorOnly) {
    return (
      <div
        className={`group border border-gray-200 rounded-lg bg-white transition-colors ${
          !isEditingBehavior ? 'hover:bg-gray-50 cursor-pointer' : ''
        }`}
        onClick={!isEditingBehavior ? () => setIsEditingBehavior(true) : undefined}
      >
        <div className="p-6 pb-4 relative">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{t('widgets.config.widgetPrompt')}</h3>
            {!isEditingBehavior && (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <PencilIcon className="w-5 h-5 text-gray-400" />
              </div>
            )}
          </div>

          {!isEditingBehavior ? (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                {t('widgets.config.widgetPrompt')}
              </label>
              <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">
                {widgetConfig.widget_prompt || '-'}
              </p>
              <p className="mt-2 text-sm text-gray-500">
                {t('widgets.config.widgetPromptDesc')}
              </p>
            </div>
          ) : (
            <div onClick={(e) => e.stopPropagation()}>
              <label htmlFor="widget_prompt" className="block text-sm font-medium text-gray-700 mb-1">
                {t('widgets.config.widgetPrompt')}
              </label>
              <textarea
                id="widget_prompt"
                name="widget_prompt"
                rows={10}
                className={INPUT_CLASSES}
                value={widgetConfig.widget_prompt}
                onChange={onConfigChange}
                placeholder={t('widgets.config.widgetPromptDesc')}
              />
              <p className="mt-2 text-sm text-gray-500">
                {t('widgets.config.widgetPromptDesc')}
              </p>
            </div>
          )}
        </div>
        {isEditingBehavior && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3" onClick={(e) => e.stopPropagation()}>
            <Button type="button" variant="secondary" size="md" onClick={() => setIsEditingBehavior(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveBehavior}
              disabled={saving}
              variant="primary"
              size="md"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8 mb-8">
      {/* Section: Basic Settings */}
      <div 
        className={`group border border-gray-200 rounded-lg bg-white transition-colors ${
          !isEditingBasic ? 'hover:bg-gray-50 cursor-pointer' : ''
        }`}
        onClick={!isEditingBasic ? handleEditBasic : undefined}
      >
        <div className="p-6 pb-4 relative">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{t('widgets.config.basicSettings')}</h3>
            {!isEditingBasic && (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <PencilIcon className="w-5 h-5 text-gray-400" />
              </div>
            )}
          </div>
          
          {!isEditingBasic ? (
            /* View Mode */
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('widgets.config.widgetType')}
                </label>
                <p className="text-sm text-gray-900 mt-1">
                  {widgetConfig.widget_type || 'Web Widget'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('widgets.config.widgetLanguage')}
                </label>
                <p className="text-sm text-gray-900 mt-1">
                  {AVAILABLE_LANGUAGES.find(lang => lang.code === (widgetConfig.widget_language || 'pl-pl'))?.name || 'Polski'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Assistant Name
                </label>
                <p className="text-sm text-gray-900 mt-1">
                  {widgetConfig.widget_assistant_name || '-'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('widgets.config.widgetTitle')}
                </label>
                <p className="text-sm text-gray-900 mt-1">
                  {widgetConfig.widget_title || '-'}
                </p>
              </div>

              {widgetConfig.widget_type !== 'API' && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    {t('widgets.config.widgetSubtitle')}
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {widgetConfig.widget_subtitle || '-'}
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Edit Mode */
            <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
              {/* Widget Type - Badge Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('widgets.config.widgetType')}
                </label>
                <ButtonGroup
                  options={[
                    { value: 'Web Widget', label: 'Web Widget' },
                    { value: 'API', label: 'API' }
                  ]}
                  value={widgetConfig.widget_type || 'Web Widget'}
                  onChange={(value) => onConfigChange({ target: { name: 'widget_type', value } })}
                  size="md"
                />
                <p className="mt-2 text-sm text-gray-500">
                  {t('widgets.config.widgetTypeDesc')}
                </p>
              </div>

              {/* Language */}
              <div>
                <label htmlFor="widget_language" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('widgets.config.widgetLanguage')}
                </label>
                <select
                  name="widget_language"
                  id="widget_language"
                  className={SELECT_CLASSES}
                  value={widgetConfig.widget_language || 'pl-pl'}
                  onChange={onConfigChange}
                >
                  {AVAILABLE_LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="widget_assistant_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Assistant Name
                </label>
                <input
                  id="widget_assistant_name"
                  name="widget_assistant_name"
                  type="text"
                  className={INPUT_CLASSES}
                  value={widgetConfig.widget_assistant_name || ''}
                  onChange={onConfigChange}
                  placeholder="Optional assistant display name"
                />
              </div>

              {/* Title */}
              <div>
                <label htmlFor="widget_title" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('widgets.config.widgetTitle')}
                </label>
                <input
                  id="widget_title"
                  name="widget_title"
                  type="text"
                  className={INPUT_CLASSES}
                  value={widgetConfig.widget_title}
                  onChange={onConfigChange}
                  placeholder={t('widgets.config.widgetTitle')}
                />
                <p className="mt-2 text-sm text-gray-500">
                  {t('widgets.config.widgetTitleDesc')}
                </p>
              </div>

              {/* Subtitle */}
              {widgetConfig.widget_type !== 'API' && (
                <div>
                  <label htmlFor="widget_subtitle" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('widgets.config.widgetSubtitle')}
                  </label>
                  <input
                    id="widget_subtitle"
                    name="widget_subtitle"
                    type="text"
                    className={INPUT_CLASSES}
                    value={widgetConfig.widget_subtitle}
                    onChange={onConfigChange}
                    placeholder={t('widgets.config.widgetSubtitle')}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    {t('widgets.config.widgetSubtitleDesc')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        {isEditingBasic && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3" onClick={(e) => e.stopPropagation()}>
            <Button type="button" variant="secondary" size="md" onClick={() => setIsEditingBasic(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveBasic}
              disabled={saving}
              variant="primary"
              size="md"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {/* Section: Content Settings */}
      <div 
        className={`group border border-gray-200 rounded-lg bg-white transition-colors ${
          !isEditingContent ? 'hover:bg-gray-50 cursor-pointer' : ''
        }`}
        onClick={!isEditingContent ? handleEditContent : undefined}
      >
        <div className="p-6 pb-4 relative">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{t('widgets.config.contentSettings')}</h3>
            {!isEditingContent && (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <PencilIcon className="w-5 h-5 text-gray-400" />
              </div>
            )}
          </div>
          
          {!isEditingContent ? (
            /* View Mode */
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('widgets.config.welcomeMessage')}
                </label>
                <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">
                  {widgetConfig.welcome_message || '-'}
                </p>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('widgets.config.supportLinkUrl')}
                </label>
                <p className="text-sm text-gray-900 mt-1">
                  {widgetConfig.widget_link || '-'}
                </p>
              </div>

              {widgetConfig.widget_type !== 'API' && widgetConfig.widget_questions && widgetConfig.widget_questions.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    {t('widgets.config.suggestedQuestions')}
                  </label>
                  <div className="space-y-2">
                    {widgetConfig.widget_questions.slice(0, 3).map((q, idx) => (
                      <p key={idx} className="text-sm text-gray-900">
                        • {q.question || q.text}
                      </p>
                    ))}
                    {widgetConfig.widget_questions.length > 3 && (
                      <p className="text-sm text-gray-500">
                        +{widgetConfig.widget_questions.length - 3} more
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Edit Mode */
            <div className="space-y-6" onClick={(e) => e.stopPropagation()}>
              {/* Welcome Message */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="welcome_message" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('widgets.config.welcomeMessage')}
                  </label>
                  <textarea
                    id="welcome_message"
                    name="welcome_message"
                    rows={3}
                    className={INPUT_CLASSES}
                    value={widgetConfig.welcome_message}
                    onChange={onConfigChange}
                    placeholder={t('widgets.config.welcomeMessageDesc')}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    First message sent in the chat conversation
                  </p>
                </div>
              </div>

              {/* Support Link */}
              <div className="border-t border-gray-200 pt-4">
                <div>
                  <label htmlFor="widget_link" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('widgets.config.supportLinkUrl')}
                  </label>
                  <input
                    type="text"
                    name="widget_link"
                    id="widget_link"
                    className={INPUT_CLASSES}
                    value={widgetConfig.widget_link}
                    onChange={onConfigChange}
                    placeholder={t('widgets.config.supportLinkUrl')}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    {t('widgets.config.supportLinkDesc')}
                  </p>
                </div>
              </div>

              {/* Suggested Questions */}
              {widgetConfig.widget_type !== 'API' && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-base font-medium text-gray-900 mb-4">{t('widgets.config.suggestedQuestions')}</h4>
                  
                  <div>
                    <p className="text-sm text-gray-600 mb-4">
                      {t('widgets.config.suggestedQuestionsDesc')}
                    </p>
                    
                    <QuestionList
                      questions={widgetConfig.widget_questions}
                      onEdit={editQuestion}
                      onRemove={removeQuestion}
                      onReorder={reorderQuestions}
                    />
                    
                    <Button
                      type="button"
                      onClick={openAddQuestionDrawer}
                      variant="primary"
                      size="md"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5 mr-2">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      {t('widgets.config.addQuestion')}
                    </Button>
                    
                    <QuestionDrawer
                      isOpen={showQuestionDrawer}
                      mode={drawerMode}
                      currentQuestion={currentQuestion}
                      onQuestionChange={handleQuestionChange}
                      onSave={addQuestion}
                      onCancel={cancelEdit}
                    />
                    
                    <p className="mt-2 text-sm text-gray-500">
                      {t('widgets.config.suggestedQuestionsDesc2')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {isEditingContent && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3" onClick={(e) => e.stopPropagation()}>
            <Button type="button" variant="secondary" size="md" onClick={handleCancelContentEdit}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveContent}
              disabled={saving}
              variant="primary"
              size="md"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetSettings;

